'use client'

import { useState } from 'react'
import { StructuredText } from 'react-datocms'
import Cta from './ui/cta'
import type { NavigationItemGroupFragment } from '@/lib/types/datocms/graphql/generated'
import Link from 'next/link'
import type { Route } from 'next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFacebook, faLinkedin, faXTwitter } from '@fortawesome/free-brands-svg-icons'
import PlusMinus from './svg/icon/PlusMinus'

type Props = {
	footerNavigationItems: NavigationItemGroupFragment[]
}
export default function FooterNavItems({ footerNavigationItems }: Readonly<Props>) {
	const [isOpen, setIsOpen] = useState(0)

	if (!footerNavigationItems || !footerNavigationItems.length) {
		return null
	}

	return (
		<div className="grid grid-cols-1 sm:grid-cols-2 py-16 pb-44 px-6 lg:px-16 lg:w-fit gap-4 md:gap-12">
			{footerNavigationItems.map((itemsGroup) => {
				return (
					<nav key={itemsGroup.id as string} className="flex flex-col gap-4">
						{/* Mobile Footer Menu Accordion */}
						{itemsGroup.items[0].__typename === 'NavigationItemRecord' ? (
							<div className="max-sm:border-b border-blue-light">
								<button
									className="w-full text-left flex items-center justify-between sm:hidden"
									onClick={() => setIsOpen(isOpen === itemsGroup.id ? 0 : itemsGroup.id)}
								>
									{/* Nav Group Title / Button */}
									<h2 className="uppercase font-mono pb-4">{itemsGroup.groupTitle}</h2>
									<PlusMinus
										isOpen={isOpen === itemsGroup.id}
										fill="white"
										className="scale-50 origin-top -mt-2"
									/>
								</button>

								{/* ABOUT */}
								<h2 className="uppercase font-mono pb-4 max-sm:hidden max-md:mt-10">
									{itemsGroup.groupTitle}
								</h2>

								<div
									className={`grid transition-all duration-300 ease-in-out overflow-hidden ${
										isOpen === itemsGroup.id
											? 'grid-rows-[1fr] pb-4'
											: 'grid-rows-[0fr] sm:grid-rows-[3fr]'
									}`}
								>
									<ul className="overflow-hidden">
										{itemsGroup.items.map((item) => {
											// Page Items
											if (item.__typename === 'NavigationItemRecord' && item.label) {
												return (
													<li
														key={item.id as string}
														className="list-none py-1.5 footer-link-hover after:bottom-1.5"
													>
														<Cta cta={item}>{item.label}</Cta>
													</li>
												)
											}
										})}
									</ul>
								</div>
							</div>
						) : (
							<>
								{/* OTHERS */}
								<h2 className="uppercase font-mono md:mb-4 max-md:mt-10">
									{itemsGroup.groupTitle}
								</h2>
								{itemsGroup.items.map((item) => {
									// Contact Info
									if (item.__typename === 'ContactInfoRecord') {
										return (
											<div
												key={item.id as string}
												className="leading-snug w-fit [&_a]:footer-link-hover after:-bottom-0.5"
											>
												<StructuredText data={item.text} />
											</div>
										)
									}
									// Social Links
									if (item.__typename === 'SocialLinkRecord') {
										return item.link ? (
											<Link
												href={item.link as Route}
												key={item.id}
												className="group flex items-center gap-3 w-fit"
											>
												{item.socialMedia === 'facebook' && (
													<FontAwesomeIcon icon={faFacebook} className="w-5" />
												)}
												{item.socialMedia === 'linkedIn' && (
													<FontAwesomeIcon icon={faLinkedin} className="w-5" />
												)}
												{item.socialMedia === 'x' && (
													<FontAwesomeIcon icon={faXTwitter} className="w-5" />
												)}
												<span className="capitalize footer-link-hover">{item.socialMedia}</span>
											</Link>
										) : null
									}
								})}
							</>
						)}
					</nav>
				)
			})}
		</div>
	)
}
